import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { LogOut, Loader2 } from "lucide-react";
import { Logo } from "../components/site/Logo";

const API = process.env.REACT_APP_BACKEND_URL;
const STATUSES = ["new", "contacted", "qualified", "closed"];
const auth = () => ({ withCredentials: true, headers: { Authorization: `Bearer ${sessionStorage.getItem("si_token") || ""}` } });
const errMsg = (d) => (typeof d === "string" ? d : Array.isArray(d) ? d.map((e) => e?.msg).join(" ") : "Login failed");

const Login = ({ onDone }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/api/auth/login`, { email, password }, { withCredentials: true });
      sessionStorage.setItem("si_token", data?.token ?? "");
      onDone();
    } catch (err) {
      toast.error(errMsg(err?.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="grid min-h-screen place-items-center bg-[#082619] px-5">
      <form onSubmit={submit} className="w-full max-w-sm rounded-2xl bg-[#F9F8F3] p-8" data-testid="admin-login-form">
        <Logo />
        <h1 className="font-display mt-8 text-2xl font-medium text-[#082619]">Enquiries desk</h1>
        <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email" className="mt-6 w-full rounded-lg border border-[#E5E1D3] bg-white px-4 py-3 text-sm" data-testid="admin-email-input" />
        <input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Password" className="mt-3 w-full rounded-lg border border-[#E5E1D3] bg-white px-4 py-3 text-sm" data-testid="admin-passcode-input" />
        <button disabled={busy} className="mt-6 w-full rounded-full bg-[#082619] py-3 text-sm font-semibold text-[#F9F8F3]" data-testid="admin-login-submit">
          {busy ? "Signing in..." : "Sign in"}
        </button>
      </form>
    </div>
  );
};

const Table = ({ items, onStatus }) => (
  <div className="overflow-x-auto rounded-2xl border border-[#E5E1D3] bg-white">
    <table className="w-full min-w-[900px] text-left text-sm" data-testid="admin-enquiries-table">
      <thead className="eyebrow !text-[0.6rem] text-[#4C6053]">
        <tr>{["Date", "Contact", "Company", "Type / Interest", "Message", "Status"].map((h) => <th key={h} className="px-4 py-4">{h}</th>)}</tr>
      </thead>
      <tbody>
        {items.map((e) => (
          <tr key={e.id} className="border-t border-[#E5E1D3] align-top" data-testid={`admin-row-${e.id}`}>
            <td className="px-4 py-4 whitespace-nowrap text-[#4C6053]">{new Date(e.created_at).toLocaleDateString("en-IN")}</td>
            <td className="px-4 py-4"><p className="font-semibold text-[#082619]">{e.name}</p><p className="text-[#4C6053]">{e.email}</p><p className="text-[#4C6053]">{e.phone}</p></td>
            <td className="px-4 py-4 text-[#082619]">{e.company}<p className="text-[#4C6053]">{e.designation}</p><p className="text-[#4C6053]">{e.location}</p></td>
            <td className="px-4 py-4 text-[#082619]">{e.enquiry_type}<p className="text-[#4C6053]">{e.interest}</p></td>
            <td className="px-4 py-4 max-w-xs text-[#4C6053]">
              {e.message}
              {Array.isArray(e.finder_profile) && e.finder_profile.length > 0 && (
                <div className="mt-3 rounded-lg bg-[#F2EFE7] p-3 text-xs" data-testid={`admin-finder-${e.id}`}>
                  <p className="eyebrow !text-[0.55rem] text-[#3f8a0f]">Facility profile (Solution Finder)</p>
                  <ul className="mt-2 space-y-1">
                    {e.finder_profile.map((p) => (
                      <li key={p.question}><span className="text-[#4C6053]">{p.question}</span> <span className="font-semibold text-[#082619]">{p.answer}</span></li>
                    ))}
                  </ul>
                  {e.finder_shortlist?.length > 0 && <p className="mt-2">Shortlist: <span className="font-semibold text-[#082619]">{e.finder_shortlist.join(", ")}</span></p>}
                </div>
              )}
            </td>
            <td className="px-4 py-4">
              <select value={e.status} onChange={(ev) => onStatus(e.id, ev.target.value)} className="rounded-full border border-[#E5E1D3] px-3 py-1.5 text-xs" data-testid={`admin-status-${e.id}`}>
                {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export default function AdminPage() {
  const [state, setState] = useState("loading");
  const [items, setItems] = useState([]);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/enquiries`, auth());
      setItems(Array.isArray(data) ? data : []);
      setState("ready");
    } catch {
      setState("login");
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const onStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/api/enquiries/${id}`, { status }, auth());
      setItems((xs) => xs.map((x) => (x.id === id ? { ...x, status } : x)));
      toast.success("Status updated");
    } catch {
      toast.error("Could not update status");
    }
  };

  const logout = async () => {
    await axios.post(`${API}/api/auth/logout`, {}, auth()).catch(() => {});
    sessionStorage.removeItem("si_token");
    setState("login");
  };

  if (state === "loading") return <div className="grid min-h-screen place-items-center"><Loader2 className="h-6 w-6 animate-spin" /></div>;
  if (state === "login") return <Login onDone={load} />;
  return (
    <div className="min-h-screen bg-[#F9F8F3] px-5 py-10 md:px-10" data-testid="admin-dashboard">
      <div className="mx-auto max-w-[1440px]">
        <div className="flex items-center justify-between">
          <Logo />
          <button onClick={logout} className="inline-flex items-center gap-2 text-sm font-medium text-[#082619]" data-testid="admin-logout"><LogOut className="h-4 w-4" />Sign out</button>
        </div>
        <h1 className="font-display mt-12 text-4xl font-medium text-[#082619]">Enquiries <span className="text-[#3f8a0f]">({items.length})</span></h1>
        <div className="mt-8">
          {items.length ? <Table items={items} onStatus={onStatus} /> : <p className="text-[#4C6053]" data-testid="admin-empty">No enquiries yet.</p>}
        </div>
      </div>
    </div>
  );
}
